import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Sparkles, ZoomIn, X, ChevronLeft, ChevronRight, Share2, BellRing, Check } from "lucide-react";
import { toast } from "sonner";
import StoreLayout from "../components/StoreLayout";
import ProductReviews from "../components/ProductReviews";
import PriceTag from "../components/PriceTag";
import TryOnModal from "../components/TryOnModal";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { getVisitorId } from "../lib/visitor";

// Guess a reasonable hex for known color names (fallback for legacy products)
function guessHex(name = "") {
  const n = name.toLowerCase().replace(/[-\s]/g, "");
  const m = {
    black: "#111111", jetblack: "#0a0a0a", white: "#F5F5F5", offwhite: "#F5F0E8",
    cream: "#FFFDD0", beige: "#D4C5A9", grey: "#6B7280", gray: "#6B7280",
    charcoal: "#374151", blue: "#1E3A8A", navy: "#1E40AF", cobalt: "#1565C0",
    red: "#DC2626", burgundy: "#881337", maroon: "#7f1d1d", green: "#16A34A",
    olive: "#65a30d", khaki: "#A16207", yellow: "#EAB308", mustard: "#D97706",
    orange: "#EA580C", purple: "#7C3AED", lavender: "#8b5cf6", pink: "#EC4899",
    coral: "#F97316", brown: "#92400E", tan: "#B45309",
  };
  return m[n] || "#27272A";
}

function ImageZoom({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
      onClick={onClose}
      data-testid="image-zoom-modal"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 text-white/80 hover:text-[#CFF048] z-10"
        data-testid="zoom-close-btn"
        aria-label="Close"
      >
        <X size={28} />
      </button>
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-[#CFF048] z-10"
            data-testid="zoom-prev-btn"
            aria-label="Previous"
          >
            <ChevronLeft size={36} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-[#CFF048] z-10"
            data-testid="zoom-next-btn"
            aria-label="Next"
          >
            <ChevronRight size={36} />
          </button>
        </>
      )}
      <img
        src={images[index]}
        alt=""
        className="max-w-[95vw] max-h-[92vh] object-contain cursor-default"
        onClick={(e) => e.stopPropagation()}
        data-testid="zoom-image"
      />
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 label-mono text-[#71717A] text-[10px]">
        // {index + 1} / {images.length} · TAP OUTSIDE / ESC TO CLOSE
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Restock enquiry state
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyPhone, setNotifyPhone] = useState("");
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setSize(r.data.sizes[0] || "");
      // default color = first color_variant's color, or first in colors array
      const firstColor = r.data.color_variants?.[0]?.color || r.data.colors?.[0] || "";
      setColor(firstColor);
      setActiveImg(0);
      setNotifyDone(false);
      api.post("/products/view", { product_id: r.data.id, visitor_id: getVisitorId() }).catch(() => {});
    });
  }, [slug]);

  useEffect(() => {
    if (user?.email && !notifyEmail) setNotifyEmail(user.email);
  }, [user, notifyEmail]);

  // ── Color variant helpers ───────────────────────────────────────────────
  const colorVariants = useMemo(() => {
    if (!p) return [];
    if (p.color_variants?.length > 0) return p.color_variants;
    const cols = p.colors?.length > 0 ? p.colors : ["Black"];
    return cols.map((c) => ({ color: c, hex: guessHex(c), images: p.images || [] }));
  }, [p]);

  const activeVariant = useMemo(
    () => colorVariants.find((cv) => cv.color === color) || colorVariants[0],
    [colorVariants, color],
  );

  const activeImages = useMemo(
    () => (activeVariant?.images?.length > 0 ? activeVariant.images : (p?.images || [])),
    [activeVariant, p],
  );

  // Social link previews (Facebook / WhatsApp / X). Link-preview bots get a pre-rendered copy of this
  // page, so these tags make a plain cultlyf.com/product/<slug> link show this product's photo and name.
  useEffect(() => {
    if (!p) return undefined;
    const SITE = "https://cultlyf.com";
    const first = (p.images || []).find(Boolean) || "";
    // djb2 hash, same as _og_hash() in backend/server.py (cache-buster when the image changes)
    let h = 5381;
    for (let i = 0; i < first.length; i += 1) h = ((h << 5) + h + first.charCodeAt(i)) >>> 0;
    const ogImage = first
      ? `${SITE}/api/og/product/${p.slug}.jpg?v=${h.toString(16)}`
      : `${SITE}/favicon-512.png`;
    const title = `${p.name} — Cult Lyf`;
    const desc = (p.description || "").trim().slice(0, 240)
      || `${p.name} — heavyweight cotton streetwear from Cult Lyf.`;
    const tags = [
      ["name", "description", desc],
      ["property", "og:type", "product"],
      ["property", "og:site_name", "Cult Lyf"],
      ["property", "og:title", title],
      ["property", "og:description", desc],
      ["property", "og:url", `${SITE}/product/${p.slug}`],
      ["property", "og:image", ogImage],
      ["property", "og:image:secure_url", ogImage],
      ["property", "og:image:type", "image/jpeg"],
      ["property", "og:image:width", "1200"],
      ["property", "og:image:height", "630"],
      ["property", "og:image:alt", p.name],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", title],
      ["name", "twitter:description", desc],
      ["name", "twitter:image", ogImage],
    ];
    const prevTitle = document.title;
    const restore = [];
    tags.forEach(([attr, key, value]) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        restore.push([el, el.getAttribute("content")]);
      } else {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        restore.push([el, null]);
      }
      el.setAttribute("content", value);
    });
    document.title = title;
    return () => {
      document.title = prevTitle;
      restore.forEach(([el, old]) => {
        if (old === null) el.remove();
        else el.setAttribute("content", old);
      });
    };
  }, [p]);

  const selectColor = (c) => { setColor(c); setActiveImg(0); };

  if (!p) return <StoreLayout><div className="p-20 text-center label-mono">// LOADING...</div></StoreLayout>;

  const onAdd = () => {
    if (!size) { toast.error("Pick a size"); return; }
    addItem({
      product_id: p.id,
      name: p.name,
      price: p.price,
      qty,
      size,
      color,
      image: activeImages[0] || p.images[0],
    });
    toast.success("ADDED TO CART", { description: `${p.name} // ${size}` });
  };

  const onBuy = () => {
    onAdd();
    navigate("/cart");
  };

  const onShare = async () => {
    // Try to attach the viewer's referral code so they earn ₹100/tee if recipient buys
    let refCode = "";
    try {
      const r = await api.get("/affiliate/me");
      refCode = r.data?.code || "";
    } catch { /* not logged in — share without ref */ }
    const url = new URL(`/api/share/product/${p.slug}`, window.location.origin);
    if (refCode) url.searchParams.set("ref", refCode);
    const shareUrl = url.toString();
    const title = `${p.name} — Cult Lyf`;
    const text = refCode
      ? `Check out ${p.name} on Cult Lyf — use code ${refCode}`
      : `Check out ${p.name} on Cult Lyf`;
    if (navigator.share) {
      try { await navigator.share({ title, text, url: shareUrl }); return; }
      catch (e) { if (e?.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("LINK COPIED", {
        description: refCode
          ? `Share it on WhatsApp / IG / X — you earn ₹100 per tee if they buy.`
          : "Paste it on WhatsApp / IG / X — preview shows the product image.",
      });
    } catch {
      toast.error("Couldn't copy. Long-press to copy manually.");
    }
  };

  const isSoldOut = (p.stock || 0) <= 0;

  const submitNotify = async () => {
    if (!notifyEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail)) {
      toast.error("Enter a valid email");
      return;
    }
    setNotifySubmitting(true);
    try {
      await api.post(`/products/${p.id}/notify-me`, {
        product_id: p.id,
        email: notifyEmail.trim(),
        name: user?.name || "",
        phone: notifyPhone.trim(),
        size: size || "",
      });
      setNotifyDone(true);
      toast.success("YOU'RE ON THE LIST", { description: "We'll email you the moment this drops back in." });
    } catch (err) {
      toast.error("COULDN'T SUBMIT", { description: err?.response?.data?.detail || "Try again." });
    } finally {
      setNotifySubmitting(false);
    }
  };

  return (
    <StoreLayout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div data-testid="product-gallery">
          <button
            type="button"
            onClick={() => setZoomOpen(true)}
            className="relative bg-[#111] aspect-square overflow-hidden border border-[#27272A] w-full group cursor-zoom-in block"
            data-testid="product-main-image"
            aria-label="Zoom image"
          >
            <img
              src={activeImages[activeImg] || p.images[0]}
              alt={p.name}
              className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isSoldOut ? "opacity-70" : ""}`}
            />
            {isSoldOut && (
              <span className="absolute top-4 left-4 bg-black border-2 border-[#CFF048] text-[#CFF048] px-3 py-1.5 font-display font-black text-xs tracking-widest pointer-events-none" data-testid="sold-out-badge">
                SOLD OUT
              </span>
            )}
            <span className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm text-white px-2 py-1 label-mono text-[10px] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <ZoomIn size={11} /> CLICK TO ZOOM
            </span>
          </button>
          {activeImages.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {activeImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border transition-colors ${activeImg === i ? "border-[#CFF048]" : "border-[#27272A] hover:border-[#555]"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:sticky lg:top-24 self-start">
          <p className="label-mono text-[#71717A]">// {p.collection.toUpperCase()} // SKU-{p.id.slice(0,6).toUpperCase()}</p>
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight mt-3 leading-none">{p.name}</h1>
          <div className="mt-4"><PriceTag price={p.price} mrp={p.mrp} size="xl" /></div>
          <p className="text-[#A1A1AA] mt-6 leading-relaxed">{p.description}</p>

          {/* Size */}
          <div className="mt-8">
            <p className="label-mono text-white mb-3">// SIZE</p>
            <div className="flex flex-wrap gap-2">
              {p.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`w-14 h-14 border font-display font-bold transition-all ${
                    size === s ? "border-[#CFF048] bg-[#CFF048] text-black" : "border-[#27272A] hover:border-white"
                  }`}
                  data-testid={`size-${s}`}
                >{s}</button>
              ))}
            </div>
            {p.size_chart && p.size_chart.length > 0 && (
              <div className="mt-4 border border-[#27272A]" data-testid="size-chart">
                <div className="bg-[#111] px-3 py-2 label-mono text-[#CFF048] flex items-center justify-between">
                  <span>{size ? `SIZE CHART // ${size} (INCHES)` : "SIZE CHART (INCHES)"}</span>
                  {size && (
                    <span className="text-[#71717A] text-[10px]">AUTO-FILTERED FOR {size}</span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0A0A0A] border-b border-[#27272A]">
                      <tr className="label-mono text-[#71717A]">
                        <th className="text-left p-3">SIZE</th>
                        <th className="text-left p-3">CHEST</th>
                        <th className="text-left p-3">SHOULDER</th>
                        <th className="text-left p-3">LENGTH</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(size ? p.size_chart.filter((r) => r.size === size) : p.size_chart).map((row, i) => (
                        <tr key={i} className="border-t border-[#27272A] font-mono-cult">
                          <td className="p-3 text-[#CFF048] font-bold">{row.size}</td>
                          <td className="p-3">{row.chest}"</td>
                          <td className="p-3">{row.shoulder}"</td>
                          <td className="p-3">{row.length}"</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Color */}
          <div className="mt-6" data-testid="color-selector">
            <div className="flex items-center justify-between mb-3">
              <p className="label-mono text-white">// COLORWAY</p>
              {color && <span className="label-mono text-[#CFF048] text-xs">{color.toUpperCase()}</span>}
            </div>
            <div className="flex flex-wrap gap-3">
              {colorVariants.map((cv) => {
                const isSelected = color === cv.color;
                const isLight = ["#FFFFFF", "#F5F5F5", "#F5F0E8", "#FFFDD0", "#F5F5DC", "#D4C5A9"].includes(cv.hex?.toUpperCase() || cv.hex);
                return (
                  <button
                    key={cv.color}
                    onClick={() => selectColor(cv.color)}
                    title={cv.color}
                    className={`relative flex flex-col items-center gap-1.5`}
                    data-testid={`color-${cv.color}`}
                  >
                    <div
                      className={`w-10 h-10 border-2 transition-all ${isSelected ? "border-[#CFF048] scale-110" : "border-[#27272A] hover:border-[#71717A]"}`}
                      style={{ backgroundColor: cv.hex || guessHex(cv.color) }}
                    >
                      {isSelected && (
                        <Check size={14} className={`w-full h-full p-1 ${isLight ? "text-black" : "text-white"}`} />
                      )}
                    </div>
                    <span className={`label-mono text-[9px] leading-none ${isSelected ? "text-[#CFF048]" : "text-[#71717A]"}`}>
                      {cv.color.length > 8 ? cv.color.slice(0, 7) + "…" : cv.color}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Qty */}
          <div className="mt-6">
            <p className="label-mono text-white mb-3">// QTY</p>
            <div className="inline-flex items-center border border-[#27272A]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} disabled={isSoldOut} className="w-12 h-12 hover:bg-[#111] disabled:opacity-40" data-testid="qty-minus"><Minus size={14}/></button>
              <div className="w-14 h-12 flex items-center justify-center font-mono-cult border-x border-[#27272A]" data-testid="qty-value">{qty}</div>
              <button onClick={() => setQty(qty + 1)} disabled={isSoldOut} className="w-12 h-12 hover:bg-[#111] disabled:opacity-40" data-testid="qty-plus"><Plus size={14}/></button>
            </div>
          </div>

          {/* CTAs / SOLD OUT WAITLIST */}
          {isSoldOut ? (
            <div className="mt-8 border-2 border-[#CFF048] bg-[#0A0A0A] p-5" data-testid="soldout-block">
              <p className="label-mono text-[#CFF048] mb-1">// SOLD OUT · ENQUIRY OPEN</p>
              <h3 className="font-display font-black text-2xl uppercase leading-none mt-2">Get first dibs on the restock.</h3>
              <p className="text-[#A1A1AA] text-sm mt-3">
                This drop moved fast. Drop your email — the moment it&apos;s back on the rack we&apos;ll shoot you a personal email before it hits the public feed.
              </p>
              {notifyDone ? (
                <div className="mt-5 border border-[#CFF048] p-4 flex items-start gap-3" data-testid="notify-success">
                  <BellRing size={18} className="text-[#CFF048] mt-0.5" />
                  <div>
                    <p className="label-mono text-[#CFF048]">// YOU&apos;RE ON THE LIST</p>
                    <p className="text-sm text-white mt-1">We&apos;ll notify <strong>{notifyEmail}</strong> at first drop. Stay Cult.</p>
                  </div>
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  <input
                    type="email"
                    placeholder="YOUR EMAIL"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className="cult-input w-full"
                    data-testid="notify-email-input"
                  />
                  <input
                    type="tel"
                    placeholder="PHONE (OPTIONAL)"
                    value={notifyPhone}
                    onChange={(e) => setNotifyPhone(e.target.value)}
                    className="cult-input w-full"
                    data-testid="notify-phone-input"
                  />
                  <button
                    onClick={submitNotify}
                    disabled={notifySubmitting}
                    className="btn-neon w-full disabled:opacity-60"
                    data-testid="notify-submit-btn"
                  >
                    <BellRing size={18} className="mr-2" />
                    {notifySubmitting ? "SUBMITTING…" : "NOTIFY ME WHEN BACK"}
                  </button>
                  <p className="label-mono text-[10px] text-[#71717A] text-center">
                    // WE ONLY USE THIS TO EMAIL YOU THE RESTOCK · NO SPAM · NO NEWSLETTERS
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={onAdd} className="btn-outline flex-1" data-testid="add-to-cart-btn">
                <ShoppingBag size={18} className="mr-2" /> ADD TO CART
              </button>
              <button onClick={onBuy} className="btn-neon flex-1" data-testid="buy-now-btn">
                BUY NOW
              </button>
            </div>
          )}

          {/* Try-on CTA */}
          <button
            onClick={() => setTryOnOpen(true)}
            className="mt-3 w-full border border-[#CFF048] text-[#CFF048] hover:bg-[#CFF048] hover:text-black transition-colors py-3 font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2"
            data-testid="tryon-open-btn"
          >
            <Sparkles size={18} /> AI TRY-ON · SEE IT ON YOU
          </button>

          {/* Share CTA */}
          <button
            onClick={onShare}
            className="mt-3 w-full border border-[#27272A] text-white hover:border-[#CFF048] hover:text-[#CFF048] transition-colors py-3 font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2"
            data-testid="share-product-btn"
          >
            <Share2 size={18} /> SHARE · WHATSAPP · INSTAGRAM · X
          </button>

          {/* Details */}
          <div className="mt-10 border-t border-[#27272A] pt-6 space-y-3">
            {[
              ["FABRIC", p.fabric || "100% heavyweight cotton, 240gsm"],
              ["FIT", p.fit || "Oversized / Drop shoulder"],
              ["PRINT", p.print_method || "Hand screen-printed neon ink"],
              ["DELIVERY", p.delivery_duration || "3–7 business days"],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between items-center text-sm border-b border-[#18181B] pb-2">
                <span className="label-mono text-[#71717A]">{k}</span>
                <span className="font-mono-cult text-white text-right max-w-[60%]">{v}</span>
              </div>
            ))}
            {/* Cancellation & Refund Policy */}
            {(p.cancellation_policy) && (
              <div className="border border-[#27272A] p-4 mt-2" data-testid="cancellation-policy">
                <p className="label-mono text-[#71717A] text-[10px] mb-2">// CANCELLATION & REFUND POLICY</p>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{p.cancellation_policy}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <ProductReviews product={p} />

      {/* AI TRY-ON — passes activeImages so the model uses the selected color */}
      <TryOnModal open={tryOnOpen} onClose={() => setTryOnOpen(false)} product={p} productImages={activeImages} />

      {/* IMAGE ZOOM LIGHTBOX */}
      {zoomOpen && (
        <ImageZoom
          images={activeImages.length > 0 ? activeImages : p.images}
          index={activeImg}
          onClose={() => setZoomOpen(false)}
          onPrev={() => setActiveImg((i) => (i - 1 + activeImages.length) % activeImages.length)}
          onNext={() => setActiveImg((i) => (i + 1) % activeImages.length)}
        />
      )}
    </StoreLayout>
  );
}
